import React, { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Button,
  FormControlLabel,
  FormGroup,
  Switch,
} from "@material-ui/core";

import "./print.css";
import { useReactToPrint } from "react-to-print";
import { findLot } from "../../../adminModel/auctions";
import { useParams } from "react-router-dom";

/* =========================================================
   RECEIPT PAGINATION
   Nakabase sa 11.69in na page. Kapag pinalitan mo ang
   taas ng row o ang letterhead, dito mo i-adjust.
========================================================= */

const FIRST_PAGE_ROWS = 7;
const NEXT_PAGE_ROWS = 10;
const SUMMARY_ALLOWANCE = 3;

const buildReceiptPages = (rows) => {
  const pages = [];
  let index = 0;

  while (index < rows.length) {
    const capacity =
      pages.length === 0 ? FIRST_PAGE_ROWS : NEXT_PAGE_ROWS;

    pages.push(rows.slice(index, index + capacity));
    index += capacity;
  }

  if (pages.length === 0) {
    pages.push([]);
  }

  const lastCapacity =
    pages.length === 1 ? FIRST_PAGE_ROWS : NEXT_PAGE_ROWS;

  // Kapag puno ang huling page, ilipat ang totals at
  // pirma sa sarili nitong page.
  if (pages[pages.length - 1].length > lastCapacity - SUMMARY_ALLOWANCE) {
    pages.push([]);
  }

  return pages;
};

/* =========================================================
   STYLES
========================================================= */

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    backgroundColor: "#f3f4f6",
    minHeight: "100vh",
    paddingTop: 75,
    paddingBottom: 40,
    boxSizing: "border-box",
  },

  toolbar: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 99999,
    backgroundColor: "#fff",
    borderBottom: "1px solid #ddd",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    padding: "10px 20px",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 15,
  },

  printButton: {
    textTransform: "none",
    fontWeight: 600,
  },

  document: {
    width: "8.27in",
    maxWidth: "calc(100% - 30px)",
    margin: "30px auto",
    backgroundColor: "transparent",
    boxSizing: "border-box",
  },

  page: {
    width: "100%",
    height: "11.69in",
    minHeight: "11.69in",
    maxHeight: "11.69in",
    padding: "0.30in",
    backgroundColor: "#fff",
    boxSizing: "border-box",
    position: "relative",
    overflow: "hidden",
    pageBreakInside: "avoid",
    breakInside: "avoid",
  },

  pageBreak: {
    pageBreakAfter: "always",
    breakAfter: "page",
    pageBreakInside: "avoid",
    breakInside: "avoid",
  },

  /* =====================================================
     EDITABLE
  ===================================================== */

  editable: {
    outline: "none",
    cursor: "text",
    borderRadius: 3,
    transition: "background-color 0.15s ease",

    "&:hover": {
      backgroundColor: "#f8fafc",
    },

    "&:focus": {
      backgroundColor: "#fff",
      boxShadow: "0 0 0 1px #90caf9",
    },

    "@media print": {
      "&:hover": {
        backgroundColor: "transparent",
      },

      "&:focus": {
        boxShadow: "none",
      },
    },
  },

  editableInline: {
    display: "inline-block",
    minWidth: 40,
    outline: "none",
    cursor: "text",
    borderRadius: 3,

    "&:hover": {
      backgroundColor: "#f8fafc",
    },

    "&:focus": {
      backgroundColor: "#fff",
      boxShadow: "0 0 0 1px #90caf9",
    },

    "@media print": {
      "&:hover": {
        backgroundColor: "transparent",
      },

      "&:focus": {
        boxShadow: "none",
      },
    },
  },

  /* =====================================================
     SHARED HEADER (receipt + certification)
  ===================================================== */

  logoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 30,
  },

  logo: {
    width: 210,
    height: "auto",
  },

  contact: {
    textAlign: "right",
    paddingTop: 10,
  },

  contactText: {
    fontSize: 11,
    fontWeight: "bold",
    fontStyle: "italic",
    margin: "0 0 4px",
    lineHeight: 1.3,
  },

  /* =====================================================
     RECEIPT — bagong layout

     Isang dokumento para sa lahat ng lots.
     Monochrome, hairline rules, walang malalaking
     color band para hindi masayang ang tinta at
     malinis pa rin kapag na-photocopy.
  ===================================================== */

  rPage: {
    width: "100%",
    height: "11.69in",
    minHeight: "11.69in",
    maxHeight: "11.69in",
    padding: "0.30in 0.30in 0.95in",
    backgroundColor: "#fff",
    boxSizing: "border-box",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    color: "#000",
    fontFamily: "Georgia, 'Times New Roman', serif",
    pageBreakInside: "avoid",
    breakInside: "avoid",
  },

  rLetterhead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingBottom: 10,
  },

  rLetterheadCompact: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    paddingBottom: 8,
  },

  rLogo: {
    width: 200,
    height: "auto",
  },

  rLogoSmall: {
    width: 150,
    height: "auto",
  },

  rContact: {
    textAlign: "right",
    paddingTop: 6,
  },

  rContactText: {
    fontSize: 10.5,
    fontStyle: "italic",
    margin: "0 0 3px",
    lineHeight: 1.35,
    color: "#000",
  },

  // Double rule — klasikong stationery detail.
  rRule: {
    borderTop: "2px solid #000",
    borderBottom: "1px solid #000",
    height: 3,
    marginBottom: 16,
  },

  rTitleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 14,
  },

  rDocTitle: {
    fontSize: 21,
    fontWeight: 700,
    letterSpacing: 2.5,
    lineHeight: 1,
    margin: 0,
  },

  rDocRef: {
    fontSize: 12,
    lineHeight: 1,
  },

  rContinued: {
    fontSize: 11,
    fontStyle: "italic",
    color: "#444",
  },

  rMeta: {
    display: "flex",
    gap: 28,
    borderTop: "1px solid #000",
    borderBottom: "1px solid #000",
    padding: "10px 0",
    marginBottom: 16,
  },

  rMetaCol: {
    flex: 1,
  },

  rMetaItem: {
    display: "flex",
    alignItems: "baseline",
    marginBottom: 5,

    "&:last-child": {
      marginBottom: 0,
    },
  },

  rMetaLabel: {
    width: 86,
    flexShrink: 0,
    fontSize: 10,
    color: "#555",
    fontFamily: "Helvetica, Arial, sans-serif",
  },

  rMetaValue: {
    flex: 1,
    fontSize: 12.5,
    fontWeight: 700,
    lineHeight: 1.3,
  },

  rBody: {
    flex: 1,
    minHeight: 0,
  },

  rTable: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },

  rTh: {
    borderTop: "1px solid #000",
    borderBottom: "1px solid #000",
    backgroundColor: "#f1f1ef",
    padding: "7px 8px",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.6,
    textAlign: "left",
    fontFamily: "Helvetica, Arial, sans-serif",
    verticalAlign: "middle",
  },

  rThRight: {
    textAlign: "right",
  },

  rTd: {
    borderBottom: "1px solid #d9d9d5",
    padding: "9px 8px",
    verticalAlign: "top",
    fontSize: 10.5,
    lineHeight: 1.35,
    height: "0.78in",
  },

  rTdLot: {
    fontSize: 12,
    fontWeight: 700,
    textAlign: "center",
  },

  rTdThumb: {
    padding: "8px 6px",
  },

  rThumb: {
    width: "100%",
    height: "0.62in",
    backgroundSize: "contain",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundColor: "#fff",
  },

  rThumbEmpty: {
    width: "100%",
    height: "0.62in",
    border: "1px solid #e2e2de",
    boxSizing: "border-box",
  },

  rArtist: {
    fontSize: 11.5,
    fontWeight: 700,
    lineHeight: 1.3,
    marginBottom: 2,
  },

  rArtTitle: {
    fontSize: 10.5,
    fontStyle: "italic",
    lineHeight: 1.3,
    color: "#1a1a1a",
  },

  rArtMeta: {
    fontSize: 9.5,
    lineHeight: 1.3,
    color: "#555",
    marginTop: 2,
  },

  rMoney: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },

  rMoneyStrong: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    fontWeight: 700,
  },

  rSummary: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 18,
  },

  rTotals: {
    width: "3.5in",
  },

  rTotalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    padding: "5px 0",
    fontSize: 11.5,
  },

  rTotalRuled: {
    borderTop: "1px solid #000",
  },

  rGrandRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    borderTop: "2px solid #000",
    borderBottom: "2px solid #000",
    padding: "8px 0",
    marginTop: 4,
  },

  rGrandLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.5,
  },

  rGrandValue: {
    fontSize: 16,
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },

  rNote: {
    marginTop: 14,
    fontSize: 9.5,
    lineHeight: 1.45,
    color: "#444",
    maxWidth: "4.4in",
    fontFamily: "Helvetica, Arial, sans-serif",
  },

  rSignBlock: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 46,
  },

  rSign: {
    width: "2.4in",
    textAlign: "center",
  },

  rSignLine: {
    borderTop: "1px solid #000",
    paddingTop: 5,
    fontSize: 10.5,
  },

  rSignCaption: {
    fontSize: 9,
    color: "#555",
    marginTop: 2,
    fontFamily: "Helvetica, Arial, sans-serif",
  },

  rFooter: {
    position: "absolute",
    left: "0.30in",
    right: "0.30in",
    bottom: "0.25in",
    borderTop: "1px solid #000",
    paddingTop: 6,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },

  rFooterAddress: {
    fontSize: 9.5,
    lineHeight: 1.4,
    color: "#000",
    textAlign: "left",

    "& p": {
      margin: 0,
    },
  },

  rFooterPage: {
    fontSize: 9.5,
    color: "#444",
    whiteSpace: "nowrap",
    fontFamily: "Helvetica, Arial, sans-serif",
  },

  /* =====================================================
     CERTIFICATION — walang binago
  ===================================================== */

  certificationPage: {
    width: "100%",
    height: "11.69in",
    minHeight: "11.69in",
    maxHeight: "11.69in",
    padding: "0.30in",
    boxSizing: "border-box",
    backgroundColor: "#fff",
    position: "relative",
    overflow: "hidden",
    pageBreakInside: "avoid",
    breakInside: "avoid",
  },

  certificationTitle: {
    width: "100%",
    textAlign: "center",
    fontSize: 30,
    fontWeight: 700,
    lineHeight: 1.1,
    margin: "0 0 14px",
    padding: 0,
    color: "#000",
  },

  certificationText: {
    width: "100%",
    maxWidth: "7.05in",
    margin: "0 auto",
    textAlign: "center",
    fontSize: 13.5,
    lineHeight: 1.28,
    color: "#000",
    boxSizing: "border-box",

    "& p": {
      margin: "0 0 8px",
      padding: 0,
      textAlign: "center",
      lineHeight: 1.28,
    },

    "& strong": {
      fontWeight: 700,
    },
  },

  certificationLot: {
    textAlign: "center",
    fontSize: 15,
    marginTop: 15,
    marginBottom: 8,
    lineHeight: 1.3,
  },

  certificationImage: {
    width: "100%",
    height: 250,
    backgroundSize: "contain",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundColor: "#fff",
    flexShrink: 0,
  },

  artworkInformation: {
    width: "6.3in",
    maxWidth: "100%",
    margin: "12px auto 0",
    textAlign: "center",
    fontFamily: "Georgia, serif",
    color: "#000",
    lineHeight: 1.4,
    boxSizing: "border-box",
    overflow: "hidden",
  },

  artistInfo: {
    fontSize: 15,
    fontWeight: "bold",
    margin: "0 0 4px",
    lineHeight: 1.3,
  },

  titleInfo: {
    fontSize: 13,
    fontStyle: "italic",
    margin: "0 0 6px",
    lineHeight: 1.3,
  },

  normalInfo: {
    fontSize: 11.5,
    margin: "0 0 4px",
    lineHeight: 1.3,
  },

  sectionInfoTitle: {
    fontSize: 11.5,
    fontWeight: "bold",
    letterSpacing: 0.8,
    margin: "9px 0 3px",
    lineHeight: 1.25,
    alignContent: "center",
  },

  sectionInfo: {
    fontSize: 10.5,
    margin: "0 auto 7px",
    maxWidth: "5.8in",
    lineHeight: 1.35,
    alignContent: "center",
  },

  certificationSignature: {
    width: 220,
    marginLeft: "auto",
    marginTop: 80,
    textAlign: "center",
    breakInside: "avoid",
    pageBreakInside: "avoid",
  },

  signatureLine: {
    borderTop: "1px solid #000",
    paddingTop: 5,
    fontSize: 12,
  },

  certificationFooter: {
    position: "absolute",
    left: "0.30in",
    right: "0.30in",
    bottom: "0.25in",
    textAlign: "center",
    marginTop: 0,
    breakInside: "avoid",
    pageBreakInside: "avoid",
  },

  footerText: {
    fontSize: 11,
    margin: "3px 0",
  },

  noData: {
    textAlign: "center",
    padding: 40,
    fontSize: 16,
  },
}));

/* =========================================================
   COMPONENT
========================================================= */

export default function AutoGrid() {
  const classes = useStyles();
  const componentRef = useRef();

  const {
    title,
    eventId,
    name,
    receiptNumber,
    eventDate,
  } = useParams();

  const [data, setData] = useState([]);

  const [state, setState] = useState({
    Provinance: false,
    property: false,
    COA: false,
  });

  /* =====================================================
     PRINT
  ===================================================== */

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  /* =====================================================
     GET LOT DATA
  ===================================================== */

  useEffect(() => {
    const storedNames = localStorage.getItem("lotView");

    if (!storedNames || !eventId) {
      setData([]);
      return;
    }

    const testData = storedNames.replace(/\n/g, ",");

    const getAuction = async () => {
      try {
        const results = await findLot(testData, eventId);

        if (!results || !results.data) {
          setData([]);
          return;
        }

        const sorted = [...results.data].sort(
          (a, b) =>
            Number(a.lot_number) - Number(b.lot_number)
        );

        setData(sorted);
      } catch (error) {
        console.error("Failed to load lots:", error);
        setData([]);
      }
    };

    getAuction();
  }, [eventId]);

  /* =====================================================
     FORMAT DATE
  ===================================================== */

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "0000-00-00") {
      return "---";
    }

    const date = new Date(dateStr);

    if (Number.isNaN(date.getTime())) {
      return dateStr;
    }

    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  const formattedDate = formatDate(eventDate);

  /* =====================================================
     FORMAT MONEY
  ===================================================== */

  const formatMoney = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return "0";
    }

    const number = parseFloat(value);

    if (Number.isNaN(number)) {
      return "0";
    }

    return number.toLocaleString();
  };

  // Para sa receipt — laging dalawang decimal.
  const formatAmount = (value) => {
    const number = parseFloat(value);

    if (Number.isNaN(number)) {
      return "0.00";
    }

    return number.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  /* =====================================================
     CLEAN HTML
  ===================================================== */

  const cleanHTML = (html) => {
    if (!html) {
      return "";
    }

    return String(html)
      .replace(/&middot;|•|·/g, "")
      .trim();
  };

  const stripTags = (html) => {
    if (!html) {
      return "";
    }

    return cleanHTML(html)
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  /* =====================================================
     SWITCH
  ===================================================== */

  const handleChange = (event) => {
    setState((prev) => ({
      ...prev,
      [event.target.name]: event.target.checked,
    }));
  };

  const printData2 = localStorage.getItem("print2");
  const printData3 = localStorage.getItem("print3");

  /* =====================================================
     SORTED DATA / RECEIPT PAGES / TOTALS
  ===================================================== */

  const sortedData = useMemo(
    () =>
      [...data].sort(
        (a, b) =>
          Number(a.lot_number) - Number(b.lot_number)
      ),
    [data]
  );

  const receiptPages = useMemo(
    () => buildReceiptPages(sortedData),
    [sortedData]
  );

  const totals = useMemo(
    () =>
      sortedData.reduce(
        (acc, item) => {
          acc.hp += Number(item.hp) || 0;
          acc.bp += Number(item.buyers_premium) || 0;
          acc.total += Number(item.total_hp) || 0;
          return acc;
        },
        { hp: 0, bp: 0, total: 0 }
      ),
    [sortedData]
  );

  const showReceipt =
    printData3 === "3" && sortedData.length > 0;

  const showCertification =
    printData2 === "2" && sortedData.length > 0;

  /* =====================================================
     EDITABLE COMPONENT
  ===================================================== */

  const Editable = ({
    children,
    className,
    html,
  }) => {
    return (
      <div
        className={`${classes.editable} ${
          className || ""
        }`}
        contentEditable
        suppressContentEditableWarning
        {...(html !== undefined
          ? {
              dangerouslySetInnerHTML: {
                __html: cleanHTML(html),
              },
            }
          : {})}
      >
        {html === undefined ? children : null}
      </div>
    );
  };

  /* =====================================================
     RECEIPT FOOTER
  ===================================================== */

  const ReceiptFooter = ({ pageNumber, pageCount }) => (
    <div className={classes.rFooter}>

      <div className={classes.rFooterAddress}>
        <p>
          G/F Eurovilla 1, Rufino Corner Legazpi Streets,
          Legazpi Village, Makati City, Philippines
        </p>
        <p>Tel no. (632) 856-2781</p>
      </div>

      <div className={classes.rFooterPage}>
        Page {pageNumber} of {pageCount}
      </div>

    </div>
  );

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <div className={classes.root}>

      {/* =================================================
          FIXED TOOLBAR
      ================================================= */}

      <div className={classes.toolbar}>

        <FormGroup row>

          <FormControlLabel
            control={
              <Switch
                checked={state.COA}
                onChange={handleChange}
                name="COA"
              />
            }
            label="COA"
          />

          <FormControlLabel
            control={
              <Switch
                checked={state.property}
                onChange={handleChange}
                name="property"
              />
            }
            label="Property"
          />

          <FormControlLabel
            control={
              <Switch
                checked={state.Provinance}
                onChange={handleChange}
                name="Provinance"
              />
            }
            label="Provenance"
          />

        </FormGroup>

        <Button
          variant="contained"
          color="primary"
          className={classes.printButton}
          onClick={handlePrint}
        >
          Print document
        </Button>

      </div>

      {/* =================================================
          DOCUMENT
      ================================================= */}

      <div
        className={classes.document}
        ref={componentRef}
      >

        {/* =============================================
            RECEIPT — lahat ng lots, nauuna
        ============================================= */}

        {showReceipt &&
          receiptPages.map((pageRows, pageIndex) => {
            const isFirstPage = pageIndex === 0;
            const isLastPage =
              pageIndex === receiptPages.length - 1;

            const needsBreak =
              !isLastPage || showCertification;

            return (
              <div
                key={`receipt-page-${pageIndex}`}
                className={`${classes.rPage} ${
                  needsBreak ? classes.pageBreak : ""
                }`}
              >

                {/* =========================
                    LETTERHEAD
                ========================= */}

                {isFirstPage ? (
                  <>
                    <div className={classes.rLetterhead}>

                      <img
                        src="/static/logo140.png"
                        alt="Leon Gallery"
                        className={classes.rLogo}
                      />

                      <div className={classes.rContact}>

                        <p className={classes.rContactText}>
                          www.leon-gallery.com
                        </p>

                        <p className={classes.rContactText}>
                          leongallerymakati@gmail.com
                        </p>

                        <p className={classes.rContactText}>
                          info@leon-gallery.com
                        </p>

                      </div>

                    </div>

                    <div className={classes.rRule} />

                    {/* =========================
                        TITLE
                    ========================= */}

                    <div className={classes.rTitleRow}>

                      <span
                        className={`${classes.rDocTitle} ${classes.editableInline}`}
                        contentEditable
                        suppressContentEditableWarning
                      >
                        RECEIPT
                      </span>

                      <span className={classes.rDocRef}>
                        No.{" "}
                        <span
                          className={classes.editableInline}
                          contentEditable
                          suppressContentEditableWarning
                        >
                          {receiptNumber || "---"}
                        </span>
                      </span>

                    </div>

                    {/* =========================
                        META
                    ========================= */}

                    <div className={classes.rMeta}>

                      <div className={classes.rMetaCol}>

                        <div className={classes.rMetaItem}>
                          <span className={classes.rMetaLabel}>
                            Issued to
                          </span>
                          <span
                            className={`${classes.rMetaValue} ${classes.editableInline}`}
                            contentEditable
                            suppressContentEditableWarning
                          >
                            {name || "---"}
                          </span>
                        </div>

                        <div className={classes.rMetaItem}>
                          <span className={classes.rMetaLabel}>
                            Auction
                          </span>
                          <span
                            className={`${classes.rMetaValue} ${classes.editableInline}`}
                            contentEditable
                            suppressContentEditableWarning
                          >
                            {title || "---"}
                          </span>
                        </div>

                      </div>

                      <div className={classes.rMetaCol}>

                        <div className={classes.rMetaItem}>
                          <span className={classes.rMetaLabel}>
                            Date
                          </span>
                          <span
                            className={`${classes.rMetaValue} ${classes.editableInline}`}
                            contentEditable
                            suppressContentEditableWarning
                          >
                            {formattedDate}
                          </span>
                        </div>

                        <div className={classes.rMetaItem}>
                          <span className={classes.rMetaLabel}>
                            Lots won
                          </span>
                          <span
                            className={`${classes.rMetaValue} ${classes.editableInline}`}
                            contentEditable
                            suppressContentEditableWarning
                          >
                            {sortedData.length}
                          </span>
                        </div>

                      </div>

                    </div>
                  </>
                ) : (
                  <>
                    <div className={classes.rLetterheadCompact}>

                      <img
                        src="/static/logo140.png"
                        alt="Leon Gallery"
                        className={classes.rLogoSmall}
                      />

                      <span className={classes.rContinued}>
                        Receipt No. {receiptNumber || "---"} — continued
                      </span>

                    </div>

                    <div className={classes.rRule} />
                  </>
                )}

                {/* =========================
                    ITEMS
                ========================= */}

                <div className={classes.rBody}>

                  {pageRows.length > 0 && (
                    <table className={classes.rTable}>

                      <colgroup>
                        <col style={{ width: "6%" }} />
                        <col style={{ width: "12%" }} />
                        <col style={{ width: "38%" }} />
                        <col style={{ width: "14%" }} />
                        <col style={{ width: "14%" }} />
                        <col style={{ width: "16%" }} />
                      </colgroup>

                      <thead>
                        <tr>

                          <th className={classes.rTh}>
                            Lot
                          </th>

                          <th className={classes.rTh}>
                            Image
                          </th>

                          <th className={classes.rTh}>
                            Artist and artwork
                          </th>

                          <th
                            className={`${classes.rTh} ${classes.rThRight}`}
                          >
                            Hammer price
                          </th>

                          <th
                            className={`${classes.rTh} ${classes.rThRight}`}
                          >
                            Buyer's premium
                          </th>

                          <th
                            className={`${classes.rTh} ${classes.rThRight}`}
                          >
                            Amount
                          </th>

                        </tr>
                      </thead>

                      <tbody>

                        {pageRows.map((value, rowIndex) => {
                          const medium = stripTags(value.MEDIUM);
                          const size = stripTags(value.size);

                          const artMeta = [medium, size]
                            .filter(Boolean)
                            .join("  ·  ");

                          return (
                            <tr
                              key={`receipt-row-${
                                value.id || value.lot_number
                              }-${rowIndex}`}
                            >

                              {/* LOT */}

                              <td
                                className={`${classes.rTd} ${classes.rTdLot}`}
                              >
                                <span
                                  className={classes.editableInline}
                                  contentEditable
                                  suppressContentEditableWarning
                                >
                                  {value.lot_number}
                                </span>
                              </td>

                              {/* IMAGE */}

                              <td
                                className={`${classes.rTd} ${classes.rTdThumb}`}
                              >
                                {value.img_url ? (
                                  <div
                                    className={classes.rThumb}
                                    style={{
                                      backgroundImage: `url(${value.img_url})`,
                                    }}
                                  />
                                ) : (
                                  <div
                                    className={classes.rThumbEmpty}
                                  />
                                )}
                              </td>

                              {/* ARTIST + ARTWORK */}

                              <td className={classes.rTd}>

                                <div
                                  className={`${classes.rArtist} ${classes.editableInline}`}
                                  contentEditable
                                  suppressContentEditableWarning
                                  dangerouslySetInnerHTML={{
                                    __html: cleanHTML(value.artist),
                                  }}
                                />

                                <div
                                  className={`${classes.rArtTitle} ${classes.editableInline}`}
                                  contentEditable
                                  suppressContentEditableWarning
                                  dangerouslySetInnerHTML={{
                                    __html: cleanHTML(value.title),
                                  }}
                                />

                                {artMeta && (
                                  <div
                                    className={`${classes.rArtMeta} ${classes.editableInline}`}
                                    contentEditable
                                    suppressContentEditableWarning
                                  >
                                    {artMeta}
                                  </div>
                                )}

                              </td>

                              {/* HAMMER */}

                              <td
                                className={`${classes.rTd} ${classes.rMoney}`}
                              >
                                <span
                                  className={classes.editableInline}
                                  contentEditable
                                  suppressContentEditableWarning
                                >
                                  {formatAmount(value.hp)}
                                </span>
                              </td>

                              {/* PREMIUM */}

                              <td
                                className={`${classes.rTd} ${classes.rMoney}`}
                              >
                                <span
                                  className={classes.editableInline}
                                  contentEditable
                                  suppressContentEditableWarning
                                >
                                  {formatAmount(value.buyers_premium)}
                                </span>
                              </td>

                              {/* AMOUNT */}

                              <td
                                className={`${classes.rTd} ${classes.rMoneyStrong}`}
                              >
                                <span
                                  className={classes.editableInline}
                                  contentEditable
                                  suppressContentEditableWarning
                                >
                                  {formatAmount(value.total_hp)}
                                </span>
                              </td>

                            </tr>
                          );
                        })}

                      </tbody>

                    </table>
                  )}

                </div>

                {/* =========================
                    TOTALS + PIRMA
                    Huling page lang.
                ========================= */}

                {isLastPage && (
                  <>
                    <div className={classes.rSummary}>

                      <div className={classes.rTotals}>

                        <div className={classes.rTotalRow}>
                          <span>Sub total (hammer price)</span>
                          <span
                            className={`${classes.rMoney} ${classes.editableInline}`}
                            contentEditable
                            suppressContentEditableWarning
                          >
                            {formatAmount(totals.hp)}
                          </span>
                        </div>

                        <div
                          className={`${classes.rTotalRow} ${classes.rTotalRuled}`}
                        >
                          <span>Buyer's premium</span>
                          <span
                            className={`${classes.rMoney} ${classes.editableInline}`}
                            contentEditable
                            suppressContentEditableWarning
                          >
                            {formatAmount(totals.bp)}
                          </span>
                        </div>

                        <div className={classes.rGrandRow}>
                          <span className={classes.rGrandLabel}>
                            Total due (PHP)
                          </span>
                          <span
                            className={`${classes.rGrandValue} ${classes.editableInline}`}
                            contentEditable
                            suppressContentEditableWarning
                          >
                            {formatAmount(totals.total)}
                          </span>
                        </div>

                        <div
                          className={`${classes.rNote} ${classes.editableInline}`}
                          contentEditable
                          suppressContentEditableWarning
                        >
                          Payment is subject to the terms and conditions
                          stated in the auction catalogue. Lots are
                          released only upon full settlement.
                        </div>

                      </div>

                    </div>

                    <div className={classes.rSignBlock}>

                      <div className={classes.rSign}>

                        <div className={classes.rSignLine}>
                          <span
                            className={classes.editableInline}
                            contentEditable
                            suppressContentEditableWarning
                          >
                            Authorized Signature
                          </span>
                        </div>

                        <div className={classes.rSignCaption}>
                          Leon Gallery Fine Art and Antiques
                        </div>

                      </div>

                    </div>
                  </>
                )}

                {/* =========================
                    FOOTER
                ========================= */}

                <ReceiptFooter
                  pageNumber={pageIndex + 1}
                  pageCount={receiptPages.length}
                />

              </div>
            );
          })}

        {/* =============================================
            CERTIFICATION
        ============================================= */}

        {showCertification &&
          sortedData.map((value, i) => (
            <div
              key={`certification-${
                value.id || value.lot_number
              }-${i}`}
              className={`${classes.certificationPage} ${
                i < sortedData.length - 1
                  ? classes.pageBreak
                  : ""
              }`}
            >

              {/* HEADER */}

              <div className={classes.logoRow}>

                <img
                  src="/static/logo140.png"
                  alt="Leon Gallery"
                  className={classes.logo}
                />

                <div className={classes.contact}>

                  <p className={classes.contactText}>
                    www.leon-gallery.com
                  </p>

                  <p className={classes.contactText}>
                    leongallerymakati@gmail.com
                  </p>

                  <p className={classes.contactText}>
                    info@leon-gallery.com
                  </p>

                </div>

              </div>

              {/* TITLE */}

              <Typography
                className={classes.certificationTitle}
              >
                <Editable>
                  CERTIFICATION
                </Editable>
              </Typography>

              {/* CERTIFICATION TEXT */}

              <div
                className={classes.certificationText}
                contentEditable
                suppressContentEditableWarning
              >

                <p>
                  This certification is issued to{" "}
                  <strong>
                    {name || "---"}
                  </strong>{" "}
                  as the winning bidder of Lot No.{" "}
                  <strong>
                    {value.lot_number}
                  </strong>{" "}
                  with description and picture below in{" "}
                  <strong>
                    {title || "---"}
                  </strong>{" "}
                  on{" "}
                  <strong>
                    {formattedDate}
                  </strong>{" "}
                  held at the G/F Eurovilla 1,
                  Rufino Corner Legazpi Streets,
                  Legazpi Village, Makati City,
                  Philippines.
                </p>

                <p>
                  This certification further confirms
                  that all rights of Lot No.{" "}
                  <strong>
                    {value.lot_number}
                  </strong>{" "}
                  has been ceded from the consignor of
                  the lot to{" "}
                  <strong>
                    {name || "---"}
                  </strong>{" "}
                  by auction in compliance with the
                  terms and conditions stipulated in
                  the catalog.
                  <br />
                  Issued{" "}
                  <strong>
                    {formattedDate}
                  </strong>
                  , Makati City, Philippines.
                </p>

              </div>

              {/* LOT NUMBER */}

              <div className={classes.certificationLot}>
                <span
                  contentEditable
                  suppressContentEditableWarning
                  className={classes.editableInline}
                >
                  Lot {value.lot_number}
                </span>
              </div>

              {/* IMAGE */}

              <div
                className={classes.certificationImage}
                style={{
                  backgroundImage: `url(${value.img_url})`,
                }}
              />

              {/* ARTWORK INFORMATION */}

              <div className={classes.artworkInformation}>

                {/* ARTIST */}

                {value.artist && (
                  <p className={classes.artistInfo}>
                    <span
                      contentEditable
                      suppressContentEditableWarning
                      className={classes.editableInline}
                      dangerouslySetInnerHTML={{
                        __html: cleanHTML(value.artist),
                      }}
                    />
                  </p>
                )}

                {/* TITLE */}

                {value.title && (
                  <p className={classes.titleInfo}>
                    <span
                      contentEditable
                      suppressContentEditableWarning
                      className={classes.editableInline}
                      dangerouslySetInnerHTML={{
                        __html: cleanHTML(value.title),
                      }}
                    />
                  </p>
                )}

                {/* SIGNED / DATED */}

                {value.signed_dated && (
                  <p className={classes.normalInfo}>
                    <span
                      contentEditable
                      suppressContentEditableWarning
                      className={classes.editableInline}
                      dangerouslySetInnerHTML={{
                        __html: cleanHTML(value.signed_dated),
                      }}
                    />
                  </p>
                )}

                {/* MEDIUM */}

                {value.MEDIUM && (
                  <p className={classes.normalInfo}>
                    <span
                      contentEditable
                      suppressContentEditableWarning
                      className={classes.editableInline}
                      dangerouslySetInnerHTML={{
                        __html: cleanHTML(value.MEDIUM),
                      }}
                    />
                  </p>
                )}

                {/* SIZE */}

                {value.size && (
                  <p className={classes.normalInfo}>
                    <span
                      contentEditable
                      suppressContentEditableWarning
                      className={classes.editableInline}
                      dangerouslySetInnerHTML={{
                        __html: cleanHTML(value.size),
                      }}
                    />
                  </p>
                )}

                {/* PROVENANCE */}

                {state.Provinance && value.provenance && (
                  <>
                    <p className={classes.sectionInfoTitle}>
                      <span
                        contentEditable
                        suppressContentEditableWarning
                        className={classes.editableInline}
                      >
                        PROVENANCE
                      </span>
                    </p>

                    <p
                      className={classes.sectionInfo}
                      contentEditable
                      suppressContentEditableWarning
                      dangerouslySetInnerHTML={{
                        __html: cleanHTML(value.provenance),
                      }}
                    />
                  </>
                )}

                {/* PROPERTY */}

                {state.property && value.property && (
                  <>
                    <p className={classes.sectionInfoTitle}>
                      <span
                        contentEditable
                        suppressContentEditableWarning
                        className={classes.editableInline}
                      >
                        PROPERTY
                      </span>
                    </p>

                    <p
                      className={classes.sectionInfo}
                      contentEditable
                      suppressContentEditableWarning
                      dangerouslySetInnerHTML={{
                        __html: cleanHTML(value.property),
                      }}
                    />
                  </>
                )}

                {/* COA */}

                {state.COA && value.coa && (
                  <>
                    <p className={classes.sectionInfoTitle}>
                      <span
                        contentEditable
                        suppressContentEditableWarning
                        className={classes.editableInline}
                      >
                        CERTIFICATE OF AUTHENTICITY
                      </span>
                    </p>

                    <p
                      className={classes.sectionInfo}
                      contentEditable
                      suppressContentEditableWarning
                      dangerouslySetInnerHTML={{
                        __html: cleanHTML(value.coa),
                      }}
                    />
                  </>
                )}

              </div>

              {/* SIGNATURE */}

              <div className={classes.certificationSignature}>
                <div className={classes.signatureLine}>
                  <span
                    contentEditable
                    suppressContentEditableWarning
                    className={classes.editableInline}
                  >
                    Authorized Signature
                  </span>
                </div>
              </div>

              {/* FOOTER */}

              <div className={classes.certificationFooter}>

                <p className={classes.footerText}>
                  G/F Eurovilla 1, Rufino Corner
                  Legazpi Streets,
                </p>

                <p className={classes.footerText}>
                  Legazpi Village, Makati City,
                  Philippines
                </p>

                <p className={classes.footerText}>
                  Tel no. (632) 856-2781
                </p>

              </div>

            </div>
          ))}

        {/* NO DATA */}

        {sortedData.length === 0 && (
          <div className={classes.noData}>
            No lot data available.
          </div>
        )}

      </div>
    </div>
  );
}
